(() => {
  if (!customElements.get('st-shopfront')) {
    customElements.define('st-shopfront', class extends HTMLElement {
      connectedCallback() {
        this.abort?.abort();
        this.abort = new AbortController();
        const signal = this.abort.signal;
        const carousel = this.querySelector('st-carousel');
        const track = this.querySelector('.st-carousel__track');
        const dots = [...this.querySelectorAll('[data-slide-to]')];
        const update = () => dots.forEach((dot, i) => {
          if (i === (carousel?.index || 0)) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        this.addEventListener('click', event => {
          const button = event.target.closest('[data-slide-to]');
          if (!button || typeof carousel?.go !== 'function') return;
          carousel.pause();
          carousel.go(Number(button.dataset.slideTo));
          update();
        }, { signal });
        track?.addEventListener('scroll', () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(update);
        }, { signal, passive:true });
        const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.videos = [...this.querySelectorAll('[data-hero-video]')].map(video => {
          const slide = video.closest('.st-carousel__slide');
          const button = slide.querySelector('[data-hero-toggle]');
          return { video, slide, button, inView: false, pending: false, failed: false,
            wanted: button.dataset.autoplay === 'true' && !motion.matches };
        });
        const isVisible = item => item.inView && !document.hidden &&
          [...track.children].indexOf(item.slide) === (carousel?.index || 0);
        const label = item => {
          if (item.failed) {
            item.button.textContent = '動画を再試行';
            item.button.setAttribute('aria-label', '動画を読み込めませんでした。再試行');
            return;
          }
          const playing = !item.video.paused;
          item.button.textContent = playing || item.pending ? '動画を停止' : '動画を再生';
          item.button.setAttribute('aria-label', playing || item.pending ? '背景動画を停止' : '背景動画を再生');
        };
        const sync = () => this.videos.forEach(item => {
          if (!isVisible(item) || !item.wanted) {
            if (!item.video.paused || item.pending) item.wanted = false;
            item.video.pause();
            label(item);
            return;
          }
          if (!item.video.paused || item.pending) return;
          item.pending = true;
          label(item);
          item.video.play().then(() => {
            if (!this.isConnected || !isVisible(item) || !item.wanted) item.video.pause();
          }).catch(() => { item.wanted = false; }).finally(() => {
            item.pending = false;
            label(item);
          });
        });
        this.videos.forEach(item => {
          item.button.hidden = false;
          item.video.muted = true;
          item.button.addEventListener('click', () => {
            if (item.failed) { item.failed = false; item.video.load(); }
            item.wanted = item.video.paused && !item.pending;
            sync();
          }, { signal });
          item.video.addEventListener('play', () => {
            if (!isVisible(item) || !item.wanted) { item.video.pause(); return; }
            carousel?.pause();
            label(item);
          }, { signal });
          item.video.addEventListener('pause', () => label(item), { signal });
          item.video.addEventListener('error', () => {
            item.wanted = false;
            item.failed = true;
            label(item);
          }, { signal });
        });
        this.addEventListener('st:slide-change', sync, { signal });
        document.addEventListener('visibilitychange', sync, { signal });
        motion.addEventListener('change', () => {
          if (motion.matches) { this.videos.forEach(item => { item.wanted = false; }); sync(); }
        }, { signal });
        this.videoObserver?.disconnect();
        if ('IntersectionObserver' in window) {
          this.videoObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
              const item = this.videos.find(item => item.slide === entry.target);
              if (item) item.inView = entry.isIntersecting && entry.intersectionRatio >= .5;
            });
            sync();
          }, { threshold: [0, .5] });
          this.videos.forEach(item => this.videoObserver.observe(item.slide));
        } else {
          // Without visibility observation, use manual playback only.
          this.videos.forEach(item => { item.inView = true; item.wanted = false; });
        }
      }
      disconnectedCallback() {
        this.abort?.abort();
        cancelAnimationFrame(this.frame);
        this.videoObserver?.disconnect();
        this.videos?.forEach(item => { item.wanted = false; item.video.pause(); });
      }
    });
  }
  if (!customElements.get('st-store-dialog')) {
    customElements.define('st-store-dialog', class extends HTMLElement {
      connectedCallback() {
        this.abort?.abort();
        this.abort = new AbortController();
        const signal = this.abort.signal;
        this.dialog = this.querySelector('dialog');
        if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
        this.querySelectorAll('[data-store-open]').forEach(link => {
          link.setAttribute('aria-haspopup', 'dialog');
          link.addEventListener('click', event => {
            event.preventDefault();
            this.opener = link;
            const map = this.dialog.querySelector('[data-store-map]');
            if (map && !map.getAttribute('src')) map.src = map.dataset.storeMap;
            this.dialog.showModal();
            this.dialog.scrollTop = 0;
            document.body.classList.add('st-store-open');
          }, { signal });
        });
        this.dialog.addEventListener('close', () => {
          document.body.classList.remove('st-store-open');
          if (this.opener?.isConnected) this.opener.focus({ preventScroll:true });
        }, { signal });
        this.dialog.addEventListener('click', event => {
          if (event.target !== this.dialog) return;
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
        }, { signal });
      }
      disconnectedCallback() {
        if (this.dialog?.open) this.dialog.close();
        this.abort?.abort();
        document.body.classList.remove('st-store-open');
      }
    });
  }
})();
